import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Tag, Space, Switch, Button, Tooltip, Typography, InputNumber, Select, Segmented, Alert, Empty, Statistic, Row, Col, message } from 'antd';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import worldJson from '../../../shared/components/world.json';
import { ReloadOutlined, GlobalOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import cdnProbeService from '../../services/cdnProbeService';

const { Text } = Typography;
const REGIONS = { CN: 'Mainland China', AS: 'Asia', EU: 'Europe', NA: 'North America', SA: 'South America', OC: 'Oceania', AF: 'Africa' };
const ms = (v) => (v == null ? '-' : `${v}ms`);
const pct = (v) => `${Math.round((v || 0) * 100)}%`;
const okColor = (r) => (r >= 0.95 ? 'success' : r >= 0.7 ? 'warning' : 'error');

echarts.registerMap('world', worldJson);

const TCP_STEPS = [[40, '#52c41a'], [100, '#a0d911'], [200, '#faad14'], [Infinity, '#f5222d']];
const FAILED_COLOR = '#8c8c8c';
const tcpColor = (p) => (!p.ok || p.tcp == null ? FAILED_COLOR : TCP_STEPS.find(([max]) => p.tcp <= max)[1]);

/** 探测点(按 TCP 上色,失败为灰色三角)→ 实际服务它的机房(蓝色,anycast 线为虚线) */
const ProbeMap = ({ map, plan, t }) => {
    const pts = (map?.points || []).filter((p) => plan === 'all' || p.group_id === plan);
    const colos = Object.fromEntries((map?.colos || []).map((c) => [c.colo, c]));
    const option = {
        tooltip: {
            trigger: 'item',
            formatter: (x) => {
                const p = x.data?.p;
                if (!p) return x.data?.colo ? `${x.data.colo}` : '';
                return `${p.where}<br/>${p.network || ''}<br/>→ ${p.colo || '-'}${p.anycast ? ' (anycast)' : ''} · ${p.ok ? `TCP ${p.tcp}ms` : t('Failed')} · ${p.ok}/${p.samples}`;
            },
        },
        geo: {
            map: 'world', roam: true, zoom: 1.15, silent: true,
            itemStyle: { areaColor: 'rgba(128,128,128,0.12)', borderColor: 'rgba(128,128,128,0.35)' },
        },
        series: [
            {
                type: 'lines', coordinateSystem: 'geo', silent: true, zlevel: 1,
                data: pts.filter((p) => p.colo && colos[p.colo]).map((p) => ({
                    coords: [[p.lon, p.lat], [colos[p.colo].lon, colos[p.colo].lat]],
                    lineStyle: { color: tcpColor(p), type: p.anycast ? 'dashed' : 'solid' },
                })),
                lineStyle: { width: 1, opacity: 0.45, curveness: 0.2 },
            },
            {
                type: 'scatter', coordinateSystem: 'geo', zlevel: 2, symbolSize: 7,
                data: pts.map((p) => ({ value: [p.lon, p.lat], p, symbol: p.ok ? 'circle' : 'triangle', itemStyle: { color: tcpColor(p) } })),
            },
            {
                type: 'effectScatter', coordinateSystem: 'geo', zlevel: 3, rippleEffect: { scale: 3 },
                symbolSize: (v) => Math.min(16, 6 + Math.sqrt(v[2] || 1)),
                itemStyle: { color: '#1677ff' },
                label: { show: true, formatter: (x) => x.data.colo, position: 'right', fontSize: 10, color: '#1677ff' },
                data: Object.values(colos).map((c) => ({ value: [c.lon, c.lat, c.samples], colo: c.colo })),
            },
        ],
    };
    return (
        <>
            <ReactECharts option={option} style={{ height: 460 }} notMerge />
            <Space size={12} wrap style={{ fontSize: 12 }}>
                {[['≤40ms', TCP_STEPS[0][1]], ['≤100ms', TCP_STEPS[1][1]], ['≤200ms', TCP_STEPS[2][1]], ['>200ms', TCP_STEPS[3][1]], [t('Failed'), FAILED_COLOR]].map(([l, c]) => (
                    <Space key={l} size={4}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: c }} />{l}</Space>
                ))}
                <Space size={4}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: '#1677ff' }} />{t('Our PoPs')}</Space>
                <Text type="secondary">- - - Anycast</Text>
            </Space>
        </>
    );
};

/** 外部全球测量(Globalping):各套餐分地区的落点与延迟、各节点 IP 外部可达性;只测量和告警 */
const GlobalProbe = () => {
    const { t } = useTranslation();
    const [hours, setHours] = useState(6);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState(null);
    const [mapPlan, setMapPlan] = useState('all');

    const load = useCallback(async () => {
        setLoading(true);
        const r = await cdnProbeService.getGlobalProbe(hours);
        setLoading(false);
        if (r.success) {
            setData(r.data);
            setSettings((s) => s || r.data.settings);
        } else message.error(r.error || t('Failed to load'));
    }, [hours, t]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const id = setInterval(load, 60000);
        return () => clearInterval(id);
    }, [load]);

    const save = async (patch) => {
        const next = { ...settings, ...patch };
        setSettings(next);
        const r = await cdnProbeService.saveGlobalProbeSettings(next);
        if (r.success) { setSettings(r.data); load(); } else message.error(r.error || t('Save failed'));
    };
    const runNow = async () => {
        const r = await cdnProbeService.runGlobalProbe();
        if (r.success) { message.success(t('Started')); setTimeout(load, 30000); } else message.error(r.error || t('Failed'));
    };

    const regionColumns = [
        { title: t('Region'), dataIndex: 'region', render: (v) => t(REGIONS[v] || v) },
        { title: t('Samples'), dataIndex: 'samples', width: 80 },
        { title: t('Success'), dataIndex: 'ok_rate', width: 90, render: (v) => <Tag color={okColor(v)}>{pct(v)}</Tag> },
        {
            title: 'TCP', dataIndex: 'tcp_median', width: 100,
            render: (v, r) => {
                const worse = v != null && r.day_tcp_median != null && v > r.day_tcp_median * 1.5 && v - r.day_tcp_median > 60;
                return <Tooltip title={`${t('24h median')}: ${ms(r.day_tcp_median)}`}><Text type={worse ? 'danger' : undefined}>{ms(v)}</Text></Tooltip>;
            },
        },
        { title: 'TCP P90', dataIndex: 'tcp_p90', width: 90, render: ms },
        { title: t('Total time'), dataIndex: 'median', width: 90, render: (v) => <Text type="secondary">{ms(v)}</Text> },
        { title: 'Anycast', dataIndex: 'anycast_share', width: 90, render: (v) => (v ? pct(v) : '-') },
        {
            title: t('Served from'), dataIndex: 'colos',
            render: (cs) => <Space size={[4, 4]} wrap>{cs.map((c) => (
                <Tag key={c.colo + c.anycast} color={c.anycast ? 'purple' : undefined}>{c.colo} {pct(c.share)}</Tag>
            ))}</Space>,
        },
    ];

    const nodeColumns = [
        { title: 'IP', dataIndex: 'ip', render: (v) => <Text code>{v}</Text> },
        { title: t('Node'), dataIndex: 'node_name', render: (v, r) => v || (r.node_id ? `#${r.node_id}` : '-') },
        { title: t('Probed from'), dataIndex: 'scope', width: 110, render: (v) => (v === 'cn' ? t('Mainland China') : v === 'world' ? t('Worldwide') : v) },
        {
            title: t('Success'), dataIndex: 'ok_rate', width: 100,
            render: (v, r) => (
                <Tooltip title={r.failures.length ? r.failures.map((f) => <div key={f.at + f.where}>{f.where} {f.code || ''}</div>) : null}>
                    <Tag color={okColor(v)}>{pct(v)}</Tag>
                </Tooltip>
            ),
        },
        { title: 'TCP', dataIndex: 'tcp_median', width: 90, render: ms },
        { title: t('Median'), dataIndex: 'median', width: 100, render: ms },
        { title: t('Last round'), dataIndex: 'last_ok', width: 100 },
    ];

    const worstColumns = [
        { title: t('Plan'), dataIndex: 'plan' },
        { title: t('Region'), dataIndex: 'region', render: (v) => t(REGIONS[v] || v) },
        { title: t('Location'), dataIndex: 'where' },
        { title: t('Network'), dataIndex: 'network', render: (v, r) => <Tooltip title={r.asn ? `AS${r.asn}` : null}>{v}</Tooltip> },
        {
            title: t('Served by'), key: 'by',
            render: (_, r) => <Space size={4}>{r.colo || '-'}{r.anycast && <Tag color="purple">Anycast</Tag>}<Text type="secondary">{r.node || r.ip}</Text></Space>,
        },
        { title: 'TCP', dataIndex: 'tcp_ms', width: 90, render: (v, r) => (r.ok ? ms(v) : <Tag color="error">{r.code || t('Failed')}</Tag>) },
        { title: t('Total time'), dataIndex: 'total_ms', width: 90, render: (v, r) => (r.ok ? <Text type="secondary">{ms(v)}</Text> : '-') },
    ];

    const s = settings;
    return (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Card
                title={<Space><GlobalOutlined />{t('Global probes')}</Space>}
                extra={(
                    <Space>
                        <Select size="small" value={hours} onChange={setHours} style={{ width: 90 }}
                            options={[2, 6, 24, 72].map((h) => ({ value: h, label: `${h}h` }))} />
                        <Button size="small" icon={<PlayCircleOutlined />} onClick={runNow} disabled={!data?.token_set}>{t('Run now')}</Button>
                        <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
                    </Space>
                )}
            >
                {data && !data.token_set && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="GLOBALPING_TOKEN" />}
                {data?.alerts?.length > 0 && (
                    <Alert type="error" showIcon style={{ marginBottom: 12 }} message={t('Active alerts')}
                        description={data.alerts.map((a) => <div key={a}><Text code>{a}</Text></div>)} />
                )}
                {s && (
                    <Space wrap size={[16, 8]} style={{ marginBottom: 16 }}>
                        <Space size={4}><Switch size="small" checked={!!s.enabled} onChange={(v) => save({ enabled: v ? 1 : 0 })} />{t('Enabled')}</Space>
                        <Space size={4}>{t('Every')}
                            <Select size="small" value={s.interval} onChange={(v) => save({ interval: v })} style={{ width: 100 }}
                                options={[15, 30, 60, 120].map((m) => ({ value: m, label: `${m} min` }))} /></Space>
                        <Tooltip title={t('Probes per plan, outside mainland China')}>
                            <Space size={4}>{t('Worldwide')}<InputNumber size="small" min={0} max={50} value={s.world} onChange={(v) => v != null && save({ world: v })} /></Space>
                        </Tooltip>
                        <Tooltip title={t('Probes per plan, mainland China')}>
                            <Space size={4}>{t('Mainland China')}<InputNumber size="small" min={0} max={20} value={s.cn} onChange={(v) => v != null && save({ cn: v })} /></Space>
                        </Tooltip>
                        <Tooltip title={t('Probes per node IP')}>
                            <Space size={4}>{t('Node IPs')}<InputNumber size="small" min={0} max={5} value={s.node} onChange={(v) => v != null && save({ node: v })} />
                                / <InputNumber size="small" min={0} max={10} value={s.node_cn} onChange={(v) => v != null && save({ node_cn: v })} /></Space>
                        </Tooltip>
                    </Space>
                )}
                {data && (
                    <Row gutter={16}>
                        <Col xs={12} md={4}><Statistic title={t('Probes / hour')} value={data.budget_per_hour} suffix={data.limits ? `/ ${data.limits.limit}` : ''} /></Col>
                        <Col xs={12} md={4}><Statistic title={t('Remaining this hour')} value={data.limits?.remaining ?? '-'} /></Col>
                        <Col xs={12} md={4}><Statistic title={t('Last hour')} value={data.probes_last_hour} /></Col>
                        <Col xs={12} md={4}><Statistic title={t('Pending')} value={data.pending} /></Col>
                        <Col xs={24} md={8}>
                            <Statistic title={t('Last run')} value={data.last_slot_at || '-'} valueStyle={{ fontSize: 16 }} />
                            {data.failed_last_hour > 0 && <Text type="danger" style={{ fontSize: 12 }}>{data.last_error}</Text>}
                        </Col>
                    </Row>
                )}
            </Card>

            {data && (
                <Card size="small" title={t('Probe map')}
                    extra={<Segmented size="small" value={mapPlan} onChange={setMapPlan}
                        options={[{ value: 'all', label: t('All') }, ...data.plans.map((p) => ({ value: p.group_id, label: p.name }))]} />}>
                    <ProbeMap map={data.map} plan={mapPlan} t={t} />
                </Card>
            )}
            {data && data.plans.length === 0 && <Card><Empty /></Card>}
            {data?.plans.map((p) => (
                <Card key={p.group_id} size="small" title={`${p.name} · ${t('by region')}`}>
                    <Table rowKey="region" size="small" pagination={false} columns={regionColumns} dataSource={p.regions} scroll={{ x: 800 }} />
                </Card>
            ))}

            {data && (
                <Card size="small" title={t('Node IPs (direct)')}>
                    <Table rowKey="ip" size="small" columns={nodeColumns} dataSource={data.nodes} pagination={{ pageSize: 20 }} scroll={{ x: 700 }} />
                </Card>
            )}
            {data && (
                <Card size="small" title={t('Slowest and failed')}>
                    <Table rowKey={(r, i) => `${r.at}-${i}`} size="small" columns={worstColumns} dataSource={data.worst} pagination={{ pageSize: 10 }} scroll={{ x: 800 }} />
                </Card>
            )}
        </Space>
    );
};

export default GlobalProbe;
